import React, { useState, useEffect, useRef } from 'react';

interface DecimalInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Input numérico que aceita vírgula e ponto como separador decimal.
 * Guarda o texto intermediário localmente e só propaga para o pai
 * quando o valor for um número válido. Exibe o número com vírgula
 * ao perder o foco (padrão pt-BR), mas aceita ponto também.
 */
const DecimalInput: React.FC<DecimalInputProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
  min,
  max,
}) => {
  // Inicializa com a representação do número, usando vírgula como separador
  const [display, setDisplay] = useState(() =>
    isFinite(value) ? String(value).replace('.', ',') : ''
  );
  // Rastreia se o input está em foco para não sobrescrever enquanto o usuário digita
  const focused = useRef(false);

  // Quando o pai atualiza o valor externamente (fora do foco), sincroniza
  useEffect(() => {
    if (!focused.current) {
      setDisplay(isFinite(value) ? String(value).replace('.', ',') : '');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Permite apenas dígitos, vírgula e ponto (e sinal negativo no início)
    if (!/^-?[\d]*[,.]?[\d]*$/.test(raw)) return;
    setDisplay(raw);

    // Normaliza separador e converte
    const normalized = raw.replace(',', '.');
    const parsed = parseFloat(normalized);
    if (!isNaN(parsed)) {
      // Respeita min/max
      if (min !== undefined && parsed < min) return;
      if (max !== undefined && parsed > max) return;
      onChange(parsed);
    }
  };

  const handleFocus = () => {
    focused.current = true;
  };

  const handleBlur = () => {
    focused.current = false;
    const normalized = display.replace(',', '.');
    const parsed = parseFloat(normalized);
    if (!isNaN(parsed)) {
      // Reformata com vírgula no blur (ex: "5." → "5", "5,5" → "5,5")
      setDisplay(String(parsed).replace('.', ','));
      onChange(parsed);
    } else {
      // Valor inválido: restaura o último número conhecido
      setDisplay(isFinite(value) ? String(value).replace('.', ',') : '0');
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
    />
  );
};

export default DecimalInput;

